import React, { useState, useEffect } from 'react';
import { Button } from '@headlessui/react';
import { 
  FaTicketAlt, FaExclamationCircle, FaClock, FaCheckCircle, FaTimes, FaExternalLinkAlt
} from 'react-icons/fa';
import SupportTicketList from './SupportTicketList';
import SupportTicketDetail from './SupportTicketDetail';
import SupportTicketHowTo from './SupportTicketHowTo';
import StatsCard from './StatsCard';
import ConfirmDialog from './ConfirmDialog';

const TICKETS_PER_PAGE = 20;

const SupportTicketsSection = ({ 
  supportTickets, 
  projects, 
  supportModules, 
  onShowToast,
  onRefresh
}) => {
  const [showSupportTicketDetail, setShowSupportTicketDetail] = useState(false);
  const [selectedSupportTicket, setSelectedSupportTicket] = useState(null);
  const [showSupportTicketHowTo, setShowSupportTicketHowTo] = useState(false);
  const [isUpdatingTicket, setIsUpdatingTicket] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [ticketList, setTicketList] = useState(supportTickets || []);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setTicketList(supportTickets || []);
  }, [supportTickets]);
  
  // Support ticket filtering states
  const [supportTicketFilters, setSupportTicketFilters] = useState({
    status: 'open', // 'open' means not closed and not resolved, 'all' means all statuses
    projectId: '',
    supportModuleId: '',
    platform: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  // Filter and sort support tickets
  const getFilteredSupportTickets = () => {
    if (!ticketList) return [];

    let filtered = [...ticketList];

    // Filter by status
    if (supportTicketFilters.status === 'open') {
      filtered = filtered.filter(ticket => ticket.status !== 'closed' && ticket.status !== 'resolved');
    } else if (supportTicketFilters.status !== 'all') {
      filtered = filtered.filter(ticket => ticket.status === supportTicketFilters.status);
    }

    // Filter by project
    if (supportTicketFilters.projectId) {
      filtered = filtered.filter(ticket => ticket.projectId?._id === supportTicketFilters.projectId);
    }

    // Filter by support module
    if (supportTicketFilters.supportModuleId) {
      filtered = filtered.filter(ticket => ticket.supportModuleId?._id === supportTicketFilters.supportModuleId);
    }

    // Filter by platform
    if (supportTicketFilters.platform) {
      filtered = filtered.filter(ticket => 
        ticket.productId?.platform === supportTicketFilters.platform || 
        ticket.platform === supportTicketFilters.platform
      );
    }

    // Sort tickets
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (supportTicketFilters.sortBy) {
        case 'createdAt':
          aValue = new Date(a.timestamps?.createdAt || a.createdAt);
          bValue = new Date(b.timestamps?.createdAt || b.createdAt);
          break;
        case 'updatedAt':
          aValue = new Date(a.timestamps?.updatedAt || a.updatedAt);
          bValue = new Date(b.timestamps?.updatedAt || b.updatedAt);
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          aValue = priorityOrder[a.priority] || 0;
          bValue = priorityOrder[b.priority] || 0;
          break;
        case 'subject':
          aValue = a.subject?.toLowerCase() || '';
          bValue = b.subject?.toLowerCase() || '';
          break;
        default:
          return 0;
      }

      if (supportTicketFilters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  const filteredTickets = getFilteredSupportTickets();
  const totalPages = Math.max(1, Math.ceil(filteredTickets.length / TICKETS_PER_PAGE));
  const activePage = Math.min(currentPage, totalPages);
  const visibleTickets = filteredTickets.slice(
    (activePage - 1) * TICKETS_PER_PAGE,
    activePage * TICKETS_PER_PAGE
  );

  const updateFilters = (changes) => {
    setSupportTicketFilters(prev => ({ ...prev, ...changes }));
    setCurrentPage(1);
  };

  const handleViewSupportTicket = (supportTicket) => {
    setSelectedSupportTicket(supportTicket);
    setShowSupportTicketDetail(true);
  };

  const handleEditSupportTicket = (supportTicket) => {
    setSelectedSupportTicket(supportTicket);
    setShowSupportTicketDetail(true);
  };

  const handleDeleteSupportTicket = (supportTicket) => {
    setTicketToDelete(supportTicket);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteSupportTicket = async () => {
    if (!ticketToDelete) return;

    try {
      const response = await fetch(`/api/support/user/support-tickets/${ticketToDelete._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        onShowToast('Support ticket deleted successfully!', 'success');
        // Refresh the data to update the list
        if (onRefresh) {
          await onRefresh();
        }
      } else {
        onShowToast('Error deleting support ticket.', 'error');
      }
    } catch (error) {
      console.error('Delete support ticket error:', error);
      if (error.message === 'Authentication failed') {
        // Handle redirect
      } else {
        onShowToast('Error occurred while deleting support ticket.', 'error');
      }
    } finally {
      setShowDeleteConfirm(false);
      setTicketToDelete(null);
    }
  };

  const handleUpdateSupportTicket = async (ticketId, updateData) => {
    setIsUpdatingTicket(true);
    try {
      const response = await fetch(`/api/support/user/support-tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update selected ticket if it's the same one
        if (selectedSupportTicket && selectedSupportTicket._id === ticketId) {
          setSelectedSupportTicket(result.supportTicket);
        }

        setTicketList(prev => prev.map(ticket => (
          ticket._id === ticketId ? result.supportTicket : ticket
        )));

        setShowSupportTicketDetail(false);
        setSelectedSupportTicket(null);
        
        // Refresh the data to update the list
        if (onRefresh) {
          onRefresh();
        }
        
        onShowToast('Support ticket updated successfully!', 'success');
        return result.supportTicket;
      } else {
        onShowToast('Error updating support ticket.', 'error');
        throw new Error('Error updating support ticket');
      }
    } catch (error) {
      console.error('Update support ticket error:', error);
      if (error.message === 'Authentication failed') {
        // Handle redirect
      } else {
        onShowToast('Error occurred while updating support ticket.', 'error');
        throw error;
      }
    } finally {
      setIsUpdatingTicket(false);
    }
  };

  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <StatsCard
          title="Total Tickets"
          value={ticketList.length}
          icon={FaTicketAlt}
        />
        
        <StatsCard
          title="Open Tickets"
          value={ticketList.filter(ticket => ticket.status === 'open').length}
          icon={FaExclamationCircle}
        />
        
        <StatsCard
          title="In Progress"
          value={ticketList.filter(ticket => ticket.status === 'in_progress').length}
          icon={FaClock}
        />
        
        <StatsCard
          title="Resolved"
          value={ticketList.filter(ticket => ticket.status === 'resolved').length}
          icon={FaCheckCircle}
        />
        
        <StatsCard
          title="Closed"
          value={ticketList.filter(ticket => ticket.status === 'closed').length}
          icon={FaTimes}
        />
      </div>

      <div className="bg-black rounded-2xl border border-gray-800 p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-white">Support Tickets</h3>
          <Button
            onClick={() => setShowSupportTicketHowTo(true)}
            className="group flex items-center px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-700 transition-all duration-200 data-hover:bg-gray-700 data-focus:ring-white border border-gray-700"
          >
            <FaExternalLinkAlt className="mr-2 h-4 w-4" />
            How it Works
          </Button>
        </div>

        {/* Filter and Sort Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-6">
          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Status</label>
            <select
              value={supportTicketFilters.status}
              onChange={(e) => updateFilters({ status: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-0"
            >
              <option value="open">Open (Not Closed/Resolved)</option>
              <option value="all">All Statuses</option>
              <option value="in_progress">In Progress</option>
              <option value="waiting_for_customer">Waiting for Customer</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          {/* Project Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Project</label>
            <select
              value={supportTicketFilters.projectId}
              onChange={(e) => updateFilters({ projectId: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-0"
            >
              <option value="">All Projects</option>
              {projects.map(project => (
                <option key={project._id} value={project._id}>{project.name}</option>
              ))}
            </select>
          </div>

          {/* Support Module Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Module</label>
            <select
              value={supportTicketFilters.supportModuleId}
              onChange={(e) => updateFilters({ supportModuleId: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-0"
            >
              <option value="">All Modules</option>
              {supportModules.map(module => (
                <option key={module._id} value={module._id}>{module.name}</option>
              ))}
            </select>
          </div>

          {/* Platform Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Platform</label>
            <select
              value={supportTicketFilters.platform}
              onChange={(e) => updateFilters({ platform: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-0"
            >
              <option value="">All Platforms</option>
              <option value="ios">iOS</option>
              <option value="android">Android</option>
              <option value="web">Web</option>
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Sort By</label>
            <select
              value={supportTicketFilters.sortBy}
              onChange={(e) => updateFilters({ sortBy: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-0"
            >
              <option value="createdAt">Created Date</option>
              <option value="updatedAt">Updated Date</option>
              <option value="status">Status</option>
              <option value="priority">Priority</option>
              <option value="subject">Subject</option>
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Order</label>
            <select
              value={supportTicketFilters.sortOrder}
              onChange={(e) => updateFilters({ sortOrder: e.target.value })}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-0"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>
        </div>

        {ticketList.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto h-12 w-12 bg-gray-900 rounded-full flex items-center justify-center mb-4">
              <FaTicketAlt className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No support tickets yet</h3>
            <p className="text-gray-400 mb-6">Support tickets will appear here when customers contact you through your support modules via API.</p>
          </div>
        ) : (
          <SupportTicketList
            supportTickets={visibleTickets}
            onView={handleViewSupportTicket}
          />
        )}

        {totalPages > 1 && (
          <nav className="flex flex-wrap items-center justify-between gap-3 mt-6" aria-label="Support ticket pages">
            <span className="text-sm text-gray-400">
              Showing {(activePage - 1) * TICKETS_PER_PAGE + 1}–{Math.min(activePage * TICKETS_PER_PAGE, filteredTickets.length)} of {filteredTickets.length}
            </span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCurrentPage(activePage - 1)}
                disabled={activePage === 1}
                className="px-3 py-2 rounded-lg border border-gray-700 bg-gray-800 text-sm text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-400">Page {activePage} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage(activePage + 1)}
                disabled={activePage === totalPages}
                className="px-3 py-2 rounded-lg border border-gray-700 bg-gray-800 text-sm text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </nav>
        )}
      </div>

      {/* Support Ticket How To Modal */}
      <SupportTicketHowTo
        isOpen={showSupportTicketHowTo}
        onClose={() => setShowSupportTicketHowTo(false)}
        projects={projects}
        supportModules={supportModules}
        onShowToast={onShowToast}
      />

      {/* Support Ticket Detail Modal */}
      <SupportTicketDetail
        isOpen={showSupportTicketDetail}
        onClose={() => {
          setShowSupportTicketDetail(false);
          setSelectedSupportTicket(null);
        }}
        supportTicket={selectedSupportTicket}
        onUpdate={handleUpdateSupportTicket}
        onDelete={(ticketId) => {
          const ticket = ticketList.find(t => t._id === ticketId);
          if (ticket) {
            handleDeleteSupportTicket(ticket);
          }
          setShowSupportTicketDetail(false);
          setSelectedSupportTicket(null);
        }}
        isLoading={isUpdatingTicket}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setTicketToDelete(null);
        }}
        onConfirm={confirmDeleteSupportTicket}
        title="Delete Support Ticket"
        message="Are you sure you want to delete this support ticket? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </div>
  );
};

export default SupportTicketsSection;
